/**
 * AGC: Alert Modal Component - Replaces browser alert() for Base App compatibility
 * AGC: Works in Base App, Farcaster, and desktop browsers
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react'

export interface AlertModalProps {
  isOpen: boolean
  message: string
  type?: 'success' | 'error' | 'info' | 'warning'
  onClose: () => void
  title?: string
}

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  message,
  type = 'info',
  onClose,
  title
}) => {
  // AGC: Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  // AGC: Determine colors and icon based on type
  const typeConfig = {
    success: {
      icon: CheckCircle,
      bgGradient: 'from-emerald-600 to-green-600',
      borderColor: 'border-emerald-500',
      textColor: 'text-emerald-100',
      iconColor: 'text-emerald-400',
      defaultTitle: 'Success'
    },
    error: {
      icon: XCircle,
      bgGradient: 'from-red-600 to-rose-600',
      borderColor: 'border-red-500',
      textColor: 'text-red-100',
      iconColor: 'text-red-400',
      defaultTitle: 'Error'
    },
    warning: {
      icon: AlertCircle,
      bgGradient: 'from-yellow-600 to-orange-600',
      borderColor: 'border-yellow-500',
      textColor: 'text-yellow-100',
      iconColor: 'text-yellow-400',
      defaultTitle: 'Warning'
    },
    info: {
      icon: Info,
      bgGradient: 'from-blue-600 to-cyan-600',
      borderColor: 'border-blue-500',
      textColor: 'text-blue-100',
      iconColor: 'text-blue-400',
      defaultTitle: 'Information'
    }
  }

  const config = typeConfig[type]
  const Icon = config.icon
  const displayTitle = title || config.defaultTitle

  // AGC: Split message by newlines for better formatting
  const messageLines = message.split('\n')

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fade-in"
      onClick={(e) => {
        // AGC: Close when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className={`bg-slate-900 border-2 ${config.borderColor} rounded-2xl shadow-2xl p-6 max-w-md w-full transform animate-scale-in`}>
        {/* AGC: Header with icon and title */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`flex-shrink-0 ${config.iconColor}`}>
            <Icon className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-xl font-bold ${config.textColor} mb-2`}>
              {displayTitle}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-slate-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Message content */}
        <div className="mb-6">
          <div className={`text-sm ${config.textColor} whitespace-pre-line`}>
            {messageLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < messageLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* AGC: Close button */}
        <button
          onClick={onClose}
          className={`w-full bg-gradient-to-r ${config.bgGradient} ${config.textColor} font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg`}
        >
          OK
        </button>
      </div>
    </div>
  )
}

// AGC: Custom animations for modal
if (typeof document !== 'undefined') {
  const styleId = 'alert-modal-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes scale-in {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      .animate-fade-in {
        animation: fade-in 0.2s ease-out;
      }
      
      .animate-scale-in {
        animation: scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
    `
    document.head.appendChild(style)
  }
}


